import { useState } from "react";
import { t } from "ttag";

import { getErrorMessage } from "metabase/api/utils/errors";
import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { Alert, Group, Icon, Stack, Switch, Text } from "metabase/ui";
import type { DatabaseId, GitSyncDatabase } from "metabase-types/api";

import {
  useListGitSyncDatabasesQuery,
  useUpdateGitSyncDatabaseMutation,
} from "../api";

type SyncedDatabasesListProps = {
  isReadOnly: boolean;
};

/**
 * The databases, each with a switch that syncs its data model (table and
 * field metadata, segments and measures), or stops syncing it.
 */
export function SyncedDatabasesList({ isReadOnly }: SyncedDatabasesListProps) {
  const { data, isLoading, error } = useListGitSyncDatabasesQuery();
  const [updateDatabase] = useUpdateGitSyncDatabaseMutation();
  const [pendingId, setPendingId] = useState<DatabaseId | null>(null);
  const [updateError, setUpdateError] = useState<unknown>(null);

  if (isLoading || error || !data) {
    return <LoadingAndErrorWrapper loading={isLoading} error={error} />;
  }

  const handleToggle = async (database: GitSyncDatabase, synced: boolean) => {
    setPendingId(database.id);
    setUpdateError(null);
    const result = await updateDatabase({ id: database.id, synced });
    if (result.error) {
      setUpdateError(result.error);
    }
    setPendingId(null);
  };

  return (
    <Stack gap="md">
      {isReadOnly && (
        <Text c="text-secondary">
          {t`This instance only imports from Git, so the databases it syncs are the ones on the branch. Their data model can't be edited here.`}
        </Text>
      )}
      {updateError != null && (
        <Alert color="error" icon={<Icon name="warning" />}>
          {getErrorMessage(updateError)}
        </Alert>
      )}
      {data.length === 0 ? (
        <Text c="text-secondary">{t`There are no databases to sync yet.`}</Text>
      ) : (
        <Stack component="ul" gap="sm" aria-label={t`Databases`}>
          {data.map((database) => (
            <Group
              component="li"
              key={database.id}
              justify="space-between"
              wrap="nowrap"
            >
              <Group gap="sm" wrap="nowrap" miw={0}>
                <Icon
                  name="database"
                  c={
                    database.is_remote_synced ? "icon-brand" : "icon-secondary"
                  }
                />
                <Text truncate>{database.name}</Text>
              </Group>
              <Switch
                aria-label={t`Sync the data model of ${database.name}`}
                checked={database.is_remote_synced}
                disabled={isReadOnly || pendingId === database.id}
                onChange={(event) =>
                  handleToggle(database, event.currentTarget.checked)
                }
              />
            </Group>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
